/**
 * NotificationsOffBanner — first-run nudge shown on the dashboard
 * when push notifications haven't been enabled on THIS device and
 * the family is still inside its onboarding window.
 *
 * Distinct from `PushRevokedBanner` (which fires after a known
 * regression — a device that *had* push and lost it). This one
 * targets the user who clicked through the welcome tour and never
 * actually toggled the switch in Settings → This device, so they
 * silently miss chore pings, event reminders and birthdays.
 *
 * Visibility rules — show only if ALL of these hold:
 *   · Browser supports push at all (`pushSupported()`)
 *   · Not a kitchen / kiosk device (those run name-attributed pings
 *     and rarely have permission anyway)
 *   · Push isn't already enabled on this device
 *   · The family was created within ONBOARDING_DAYS (default 7)
 *   · The user hasn't tapped "Not now" in this browser session
 *
 * Dismiss persists in `localStorage` so "Not now" survives a tab
 * close — first-run banners are noisy when they keep reappearing.
 * "Turn on" attempts the inline enable; on success the banner
 * disappears for good (push is now on, so the visibility check
 * fails on subsequent renders).
 */

import React, { useEffect, useState } from "react";
import { BellOff, X } from "lucide-react";
import { toast } from "sonner";
import {
  pushSupported,
  pushSubscribedHere,
  enablePush,
} from "@/lib/push";
import { isKioskDevice } from "@/lib/kioskMode";
import { useI18n } from "@/lib/i18n";

const ONBOARDING_DAYS = 7;
const DISMISS_KEY = "ff_notif_off_dismissed_until";

function dismissedNow() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() < ts;
  } catch {
    return false;
  }
}

export default function NotificationsOffBanner({ settings, viewerId, onRefresh }) {
  const { t } = useI18n();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    const check = async () => {
      // No created_at = legacy family or settings still loading —
      // stay quiet rather than nag retroactively.
      const createdIso = settings?.family_created_at;
      if (!createdIso) return;
      const created = Date.parse(createdIso);
      if (!Number.isFinite(created)) return;
      const ageDays = (Date.now() - created) / (1000 * 60 * 60 * 24);
      if (ageDays > ONBOARDING_DAYS) return;
      if (isKioskDevice()) return;
      if (!pushSupported()) return;
      if (dismissedNow()) return;
      let subscribedHere = false;
      try {
        subscribedHere = await pushSubscribedHere();
      } catch {
        /* SW unreachable — bail out rather than nag */
        return;
      }
      if (!alive) return;
      if (!subscribedHere) setShow(true);
    };
    check();
    return () => {
      alive = false;
    };
  }, [settings?.family_created_at]);

  if (!show) return null;

  const enable = async () => {
    setBusy(true);
    try {
      await enablePush(viewerId);
      toast.success("Notifications on");
      setShow(false);
      onRefresh && onRefresh();
    } catch (e) {
      // Common failures: permission denied at the browser level,
      // service worker unreachable, VAPID misconfig. Show the message
      // verbatim — the underlying enablePush() already classifies the
      // common cases into human-readable text.
      toast.error(e?.message || "Couldn't turn notifications on");
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    try {
      // 7-day snooze. Plenty of time for someone who genuinely
      // doesn't want push to set it up later — and the banner is
      // self-expiring anyway once the family is older than
      // ONBOARDING_DAYS.
      const until = Date.now() + 7 * 24 * 60 * 60 * 1000;
      localStorage.setItem(DISMISS_KEY, String(until));
    } catch {
      /* ignore */
    }
    setShow(false);
  };

  return (
    <div
      className="ff-card p-4 mb-4 flex items-start gap-3 border border-[color:var(--line-soft)] bg-[color:var(--paper-2)]"
      data-testid="notifications-off-banner"
    >
      <BellOff className="w-5 h-5 mt-0.5 shrink-0 text-[color:var(--ink-2)]" strokeWidth={2} />
      <div className="flex-1 min-w-0">
        <div className="font-display-italic text-xl leading-snug">
          {t({ en: "Notifications are off on this device.", da: "Notifikationer er slået fra på denne enhed." })}
        </div>
        <div className="text-[13px] text-[color:var(--ink-2)] leading-snug mt-1">
          {t({
            en: "Turn them on so event reminders, birthdays and chore pings land at the right time. You can always change this in Settings → This device.",
            da: "Slå dem til, så påmindelser om begivenheder, fødselsdage og pligter kommer på det rigtige tidspunkt. Du kan altid ændre det under Indstillinger → Denne enhed.",
          })}
        </div>
        <div className="flex gap-2 flex-wrap mt-3">
          <button
            type="button"
            onClick={enable}
            disabled={busy}
            className="ff-btn ff-btn-primary"
            data-testid="notifications-off-enable"
          >
            {busy
              ? t({ en: "Enabling…", da: "Aktiverer…" })
              : t({ en: "Turn on", da: "Slå til" })}
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="ff-btn"
            data-testid="notifications-off-dismiss-btn"
          >
            {t({ en: "Not now", da: "Ikke nu" })}
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="text-[color:var(--ink-3)] hover:text-[color:var(--ink-2)] -mt-1 -mr-1 p-1 shrink-0"
        aria-label={t({ en: "Dismiss", da: "Luk" })}
        data-testid="notifications-off-dismiss"
      >
        <X className="w-4 h-4" strokeWidth={2} />
      </button>
    </div>
  );
}
